import { useEffect, useState, useRef } from 'react';
import useOnbaseApi from './useOnbaseApi';

interface MetroConfigState {
  data: unknown;
  loading: boolean;
  error: string | null;
  docId: string | null;
}

// Cache for API calls to prevent duplicate requests
const apiCache = new Map<string, Promise<unknown>>();
// Track processed docIds to prevent any duplicate processing
const processedDocIds = new Set<string>();
// Track active API requests to prevent multiple concurrent calls
const activeRequests = new Set<string>();

export const useMetroConfigSuspense = (docId: string | null, forceRefresh: boolean = false) => {
  const [state, setState] = useState<MetroConfigState>(() => {
    // Initialize with localStorage data if available and not forcing refresh
    if (!docId || forceRefresh) return { data: null, loading: !!docId, error: null, docId };
    
    try {
      const raw = localStorage.getItem('metro-config-cache');
      if (raw) {
        const parsed = JSON.parse(raw);
        const matches = parsed?.data && (parsed.docId === docId || !parsed.docId);
        if (matches) {
          processedDocIds.add(docId);
          return { data: parsed.data, loading: false, error: null, docId };
        }
      }
    } catch {/* ignore parse errors */}
    
    return { data: null, loading: !!docId, error: null, docId };
  });

  const { getMetroConfig } = useOnbaseApi();

  // Track latest resolved data/docId in refs to allow them in dependency list without triggering
  const latestDataRef = useRef<unknown>(state.data);
  const latestDocIdRef = useRef<string | null>(state.docId);
  latestDataRef.current = state.data;
  latestDocIdRef.current = state.docId;

  useEffect(() => {
    if (!docId) {
      setState({ data: null, loading: false, error: null, docId: null });
      return;
    }

    // Create a unique key for this request (includes forceRefresh state)
    const requestKey = `${docId}-${forceRefresh}`;

    // Always fetch if forceRefresh is true, but prevent duplicate force refreshes
    if (forceRefresh) {
      // Clear existing cache and processed state for this docId
      apiCache.delete(docId);
      processedDocIds.delete(docId);
      
      // Check if we already have a force refresh request in progress
      if (processedDocIds.has(requestKey)) {
        console.log('useMetroConfigSuspense: Force refresh already in progress for docId:', docId);
        return;
      }
    }

    // Skip if already processed this docId and not forcing refresh
    if (processedDocIds.has(docId) && !forceRefresh) {
      return;
    }

    // Skip if we already have data for this docId and not forcing refresh
    if (latestDataRef.current && latestDocIdRef.current === docId && !forceRefresh) {
      return;
    }

    // Mark as processed to prevent future runs (use requestKey for force refresh)
    const trackingKey = forceRefresh ? requestKey : docId;
    if (processedDocIds.has(trackingKey)) {
      console.log('useMetroConfigSuspense: Request already processed for key:', trackingKey);
      return;
    }
    processedDocIds.add(trackingKey);
    processedDocIds.add(docId); // Also add docId to prevent regular fetches

    // IMMEDIATELY set loading to true when API is about to start
    console.log('useMetroConfigSuspense: API call starting for docId:', docId, 'forceRefresh:', forceRefresh);
    setState(prev => ({ 
      ...prev, 
      loading: true, 
      error: null, 
      docId 
    }));

    // Check if we already have a request in flight for this docId (skip if forceRefresh)
    if (!forceRefresh && apiCache.has(docId)) {
      console.log('useMetroConfigSuspense: Using cached promise for docId:', docId);
      
      apiCache.get(docId)!
        .then((data) => {
          console.log('useMetroConfigSuspense: Cached API success for docId:', docId);
          setState({
            data,
            loading: false,
            error: null,
            docId,
          });
        })
        .catch((error) => {
          console.error('useMetroConfigSuspense: Cached API error for docId:', docId, error);
          setState({
            data: null,
            loading: false,
            error: error.message ?? 'Failed to load configuration',
            docId,
          });
        });
      return;
    }

    // Start new API call
    console.log('useMetroConfigSuspense: Starting new API call for docId:', docId, 'forceRefresh:', forceRefresh);

    // Prevent duplicate API calls for the same docId
    if (activeRequests.has(docId)) {
      console.log('useMetroConfigSuspense: API call already active for docId:', docId);
      return;
    }
    activeRequests.add(docId);

    const apiPromise = getMetroConfig(docId)
      .then((response) => {
        console.log('useMetroConfigSuspense: API success for docId:', docId, response);
        return response.data;
      })
      .catch((error) => {
        console.error('useMetroConfigSuspense: API error for docId:', docId, error);
        throw error;
      })
      .finally(() => {
        // Always remove from active requests when done
        activeRequests.delete(docId);
      });

    // Cache the promise
    apiCache.set(docId, apiPromise);

    // Handle the result
    apiPromise
      .then((data) => {
        console.log('useMetroConfigSuspense: Setting success state for docId:', docId);
        
        // Always update localStorage with fresh data (especially when forceRefresh is true)
        try {
          const cacheData = { data, docId };
          localStorage.setItem('metro-config-cache', JSON.stringify(cacheData));
          console.log('useMetroConfigSuspense: Updated localStorage cache for docId:', docId);
        } catch (e) {
          console.warn('useMetroConfigSuspense: Failed to update localStorage cache:', e);
        }
        
        setState({
          data,
          loading: false, // STOP loading when API completes successfully
          error: null,
          docId,
        });
      })
      .catch((error) => {
        console.log('useMetroConfigSuspense: Setting error state for docId:', docId);
        setState({
          data: null,
          loading: false, // STOP loading when API fails
          error: error.message ?? 'Failed to load configuration',
          docId,
        });
        // Remove failed request from cache
        apiCache.delete(docId);
      });
  }, [docId, forceRefresh, getMetroConfig]);

  return state;
};

// Function to clear cache (useful for retry scenarios)
export const clearMetroConfigCache = (docId?: string) => {
  if (docId) {
    apiCache.delete(docId);
    processedDocIds.delete(docId);
    activeRequests.delete(docId);
    // Also clear force refresh tracking keys
    processedDocIds.delete(`${docId}-true`);
    processedDocIds.delete(`${docId}-false`);
  } else {
    apiCache.clear();
    processedDocIds.clear();
    activeRequests.clear();
  }
};
